import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Truck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Field, Input, Textarea } from '@/components/ui/form';
import { Card, CardHeader, Dialog, DialogContent, EmptyState } from '@/components/ui/primitives';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

export interface Supplier {
  id: string;
  name: string;
  contact: string | null;
  phone: string | null;
  email: string | null;
  country: string | null;
  notes: string | null;
  isActive: boolean;
  batches: number;
  products: number;
}

export function useSuppliers() {
  return useQuery({ queryKey: ['suppliers'], queryFn: () => api.get<Supplier[]>('/suppliers'), staleTime: 60_000 });
}

export function SuppliersCard() {
  const { can } = useAuth();
  const suppliers = useSuppliers();
  const [open, setOpen] = useState(false);
  return (
    <Card className="mb-6">
      <CardHeader
        title="ספקים"
        icon={<Truck />}
        count={suppliers.data?.length}
        actions={can('inventory.write') && <Button size="sm" variant="ghost" onClick={() => setOpen(true)}><Plus /> ספק חדש</Button>}
      />
      {!suppliers.data?.length ? (
        <EmptyState title="אין ספקים" description="הוסיפו את מגדלי ויצרני המאצ׳ה שמהם אתם קונים" />
      ) : (
        <ul className="divide-y divide-line/70 text-sm">
          {suppliers.data.map((s) => (
            <li key={s.id} className="flex flex-wrap items-center gap-2 px-4 py-2.5">
              <span className="font-semibold">{s.name}</span>
              <span className="text-muted">{[s.country, s.contact, s.phone, s.email].filter(Boolean).join(' · ')}</span>
              <span className="ms-auto text-xs text-muted">{s.products} מוצרים · {s.batches} אצוות</span>
            </li>
          ))}
        </ul>
      )}
      <SupplierDialog open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function SupplierDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ name: '', contact: '', phone: '', email: '', country: '', notes: '' });
  const save = useMutation({
    mutationFn: () => api.post('/suppliers', f),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      toast.success('הספק נוסף');
      setF({ name: '', contact: '', phone: '', email: '', country: '', notes: '' });
      onOpenChange(false);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="ספק חדש">
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="grid gap-4 sm:grid-cols-2">
          <Field label="שם הספק" required className="sm:col-span-2">{(id) => <Input id={id} value={f.name} onChange={set('name')} />}</Field>
          <Field label="איש קשר">{(id) => <Input id={id} value={f.contact} onChange={set('contact')} />}</Field>
          <Field label="מדינה">{(id) => <Input id={id} value={f.country} onChange={set('country')} />}</Field>
          <Field label="טלפון">{(id) => <Input id={id} dir="ltr" value={f.phone} onChange={set('phone')} />}</Field>
          <Field label="אימייל">{(id) => <Input id={id} dir="ltr" type="email" value={f.email} onChange={set('email')} />}</Field>
          <Field label="הערות" className="sm:col-span-2">{(id) => <Textarea id={id} rows={2} value={f.notes} onChange={set('notes')} />}</Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
            <Button variant="secondary" onClick={() => onOpenChange(false)}>ביטול</Button>
            <Button type="submit" loading={save.isPending}>שמירה</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
